import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

const useVideoBatchLoader = (
  orderedVideo,
  loadedUrls,
  containerElement,
  isVideoPlaying,
  setCurrentTime,
  currentUrlBatch,
  setCurrentUrlBatch,
  seekToTime,
  setIsVideoPlaying,
  forceReloadBatch,
  setSelectedVersion,
  setResetAll,
  fps,
) => {
  const [currentVideoBatch, setCurrentVideoBatch] = useState(0);
  const [loadedVideoBatches, setLoadedVideoBatches] = useState(new Set());
  const [startedLoadingVideoBatches, setStartedLoadingVideoBatches] = useState(
    new Set(),
  ); // Track which batches we've started loading
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isNewVideoVisible, setIsNewVideoVisible] = useState(true);
  const loadedVideos = useRef({});
  const isVideoPlayingRef = useRef(isVideoPlaying);
  const currentVideoIndexRef = useRef(currentVideoIndex);

  // Update refs whenever values change
  useEffect(() => {
    isVideoPlayingRef.current = isVideoPlaying;
  }, [isVideoPlaying]);

  useEffect(() => {
    currentVideoIndexRef.current = currentVideoIndex;
  }, [currentVideoIndex]);
  /*
  *
  *
  VIDEO PLAYBACK LOGIC STARTS HERE
  *
  *
  */

  // Handle seeking to specific time
  useEffect(() => {
    setIsNewVideoVisible(false);
    let timeBuffer;
    //check the video for that time
    const result = orderedVideo.find(
      (eachvideo) =>
        eachvideo.fpsStart / fps <= seekToTime &&
        eachvideo.fpsEnd / fps >= seekToTime,
    );
    setSelectedVersion(result);
    if (result) {
      setCurrentVideoIndex(result.index);
      Object.values(loadedVideos.current).forEach((video) => {
        if (video.dataset.shotIndex !== result.index) {
          video.currentTime = orderedVideo[video.dataset.shotIndex].start;
        }
      });
      const loadedVideo = loadedVideos.current[result.index];
      if (loadedVideo) {
        if (result.index !== 0) {
          timeBuffer = seekToTime - orderedVideo[result.index - 1].fpsEnd / fps;
        } else {
          timeBuffer = seekToTime;
        }
        handleElementVisiblity(result.index);
        loadedVideo.currentTime = timeBuffer;
      }
      const nextVideoBatchNumber = parseInt(result.videoBatchNumber);
      const nextUrlBatchNumber = parseInt(result.urlBatchNumber);
      //check if we need to update currentvideobatch and currenturlbatch
      if (nextVideoBatchNumber !== currentVideoBatch) {
        setCurrentVideoBatch(nextVideoBatchNumber);
      }
      if (nextUrlBatchNumber !== currentUrlBatch) {
        setCurrentUrlBatch(nextUrlBatchNumber);
      }
    }
  }, [seekToTime]);

  useEffect(() => {
    const currentVideo = loadedVideos.current[currentVideoIndex];
    if (!currentVideo) return;
    if (currentVideo.dataset.error === "true") {
      return;
    }
    if (isVideoPlaying) {
      currentVideo.play();
    } else {
      currentVideo.pause();
    }
  }, [isVideoPlaying]);

  const playNextVideo = useCallback(
    (playerIndex) => {
      if (!isVideoPlayingRef.current) {
        return;
      }

      // Safety check for valid index
      if (playerIndex < 0 || playerIndex >= orderedVideo.length) {
        return;
      }

      const nextVideo = loadedVideos.current[playerIndex];
      if (!nextVideo) return;

      nextVideo.play();
      const nextVideoBatchNumber = parseInt(nextVideo.dataset.videoBatchNumber);
      const nextUrlBatchNumber = parseInt(nextVideo.dataset.urlBatchNumber);
      //check if we need to update currentvideobatch and currenturlbatch
      if (nextVideoBatchNumber !== currentVideoBatch) {
        setCurrentVideoBatch(nextVideoBatchNumber);
      }
      if (nextUrlBatchNumber !== currentUrlBatch) {
        setCurrentUrlBatch(nextUrlBatchNumber);
      }
      //update selected version
      setSelectedVersion(orderedVideo[playerIndex]);
    },
    [currentVideoBatch, currentUrlBatch, orderedVideo.length],
  );

  const playPrevious = useCallback(() => {
    const currentIndex = currentVideoIndexRef.current;
    if (currentIndex <= 0) return; // Don't go below 0

    const currentVideo = loadedVideos.current[currentIndex];
    if (!currentVideo) return;
    currentVideo.pause();
    currentVideo.currentTime = 0;

    const newIndex = currentIndex - 1;
    setCurrentVideoIndex(newIndex);
    handleElementVisiblity(newIndex);
    playNextVideo(newIndex);
  }, [orderedVideo, playNextVideo]);

  const playNext = useCallback(() => {
    const currentIndex = currentVideoIndexRef.current;
    if (currentIndex >= orderedVideo.length - 1) return; // Don't go beyond the last video

    const currentVideo = loadedVideos.current[currentIndex];
    if (!currentVideo) return;
    currentVideo.pause();
    currentVideo.currentTime = 0;

    const newIndex = currentIndex + 1;
    setCurrentVideoIndex(newIndex);
    handleElementVisiblity(newIndex);
    playNextVideo(newIndex);
  }, [orderedVideo, playNextVideo]);

  // console.log(containerElement?.children);

  const handleElementVisiblity = (playerIndex) => {
    const FADE_OUT_DURATION = 40; // Longer fade out
    const FADE_IN_DURATION = 10; // Shorter fade in

    // Safety check for valid index
    if (playerIndex < 0 || playerIndex >= orderedVideo.length) {
      return;
    }

    const nextVideo = loadedVideos.current[playerIndex];

    if (nextVideo) {
      // Prepare the new video
      nextVideo.style.opacity = "0";
      nextVideo.style.zIndex = "2";

      // Start fading out current videos first
      Object.values(loadedVideos.current).forEach((video) => {
        if (parseInt(video.dataset.shotIndex) !== parseInt(playerIndex)) {
          video.style.transition = `opacity ${FADE_OUT_DURATION}ms ease-out`;
          video.style.opacity = "0";
          video.style.zIndex = "1";
        }
      });

      // Start fading in new video immediately but with its own timing
      nextVideo.style.transition = `opacity ${FADE_IN_DURATION}ms ease-in`;
      nextVideo.style.opacity = "1";
    }
    setIsNewVideoVisible(true);
  };

  // Polling function to check current video time
  useEffect(() => {
    let intervalId;
    if (isVideoPlayingRef.current) {
      intervalId = setInterval(() => {
        const currentVideo = loadedVideos.current[currentVideoIndexRef.current];
        if (!currentVideo) return;

        const duration = currentVideo.duration;
        const currentTime = currentVideo.currentTime;

        if (currentTime === 0) {
          return;
        }

        if (currentTime < duration) {
          // Update global time
          const currentGlobalTime =
            orderedVideo[currentVideoIndexRef.current].previousEnd +
            currentTime;
          setCurrentTime(currentGlobalTime);
        } else {
          // Current video has ended - handle transition smoothly
          const currentIndex = currentVideoIndexRef.current;
          currentVideo.pause();
          currentVideo.currentTime = orderedVideo[currentIndex].start;

          if (currentIndex !== orderedVideo.length - 1) {
            const nextIndex = currentIndex + 1;
            const nextVideo = loadedVideos.current[nextIndex];

            if (nextVideo) {
              // Pre-buffer the next video and start it immediately
              nextVideo.currentTime = 0;
              handleElementVisiblity(nextIndex);

              // Start playing the next video immediately
              nextVideo
                .play()
                .then(() => {
                  setCurrentVideoIndex(nextIndex);
                  setSelectedVersion(orderedVideo[nextIndex]);
                })
                .catch((e) => {
                  console.error("Error playing next video:", e);
                  setCurrentVideoIndex(nextIndex);
                });

              // Update batch numbers
              const nextVideoBatchNumber = parseInt(
                nextVideo.dataset.videoBatchNumber,
              );
              const nextUrlBatchNumber = parseInt(
                nextVideo.dataset.urlBatchNumber,
              );
              if (nextVideoBatchNumber !== currentVideoBatch) {
                setCurrentVideoBatch(nextVideoBatchNumber);
              }
              if (nextUrlBatchNumber !== currentUrlBatch) {
                setCurrentUrlBatch(nextUrlBatchNumber);
              }
            }
          } else {
            setIsVideoPlaying(false);
            setCurrentVideoIndex(0);
            handleElementVisiblity(0);
          }
        }
      }, 100); // Check every 100ms
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isVideoPlaying]); // Only restart when play/pause changes

  /*
  *
  *
  VIDEO PLAYBACK LOGIC ENDS HERE
  *
  *
  */

  /*
  *
  *
  BATCH LOADING VIDEO LOGIC STARTS HERE
  *
  *
  */
  const cleanupBatches = (currentBatch) => {
    const thisBatch = parseInt(currentBatch);

    const batchesToKeep = [thisBatch, thisBatch + 1];
    if (Object.keys(loadedVideos.current).length === 0) {
      return;
    }

    // Cleanup video elements that are no longer needed (batches outside the window)
    Object.entries(loadedVideos.current).forEach(
      ([shotIndex, videoElement]) => {
        const videoBatchNumber = parseInt(
          videoElement.dataset.videoBatchNumber,
        );

        if (!batchesToKeep.includes(videoBatchNumber)) {
          // Remove from DOM
          if (videoElement.parentNode) {
            // Double check we have the right element using aria-label
            const expectedAriaLabel = `${videoBatchNumber}-${shotIndex}`;

            if (videoElement.ariaLabel === expectedAriaLabel) {
              videoElement.parentNode.removeChild(videoElement);
            } else {
              console.warn(
                `Video element aria-label mismatch: expected ${expectedAriaLabel}, got ${videoElement.ariaLabel}`,
              );
            }
          }

          // Remove from loadedVideos object
          delete loadedVideos.current[shotIndex];
        }
      },
    );
    //if not in batches to keep remove it from loaded video batches
    setLoadedVideoBatches(
      (prev) =>
        new Set([...prev].filter((batch) => batchesToKeep.includes(batch))),
    );
  };

  // Function to handle URL expiry errors and refetch batch
  const handleUrlExpiryAndRefetch = useCallback(
    (batchNumber, shotRep) => {
      // Get the urlBatchNumber for this video batch
      const urlBatchNumber = shotRep?.urlBatchNumber;
      if (urlBatchNumber === undefined) {
        console.error("Could not determine urlBatchNumber for refetch");
        return;
      }
      // Remove all current videos from the container
      const videosToRemove = containerElement?.querySelectorAll("video");
      videosToRemove?.forEach((video) => {
        video.remove();
      });

      // Remove all current videos from the loaded videos object
      Object.keys(loadedVideos.current).forEach((key) => {
        delete loadedVideos.current[key];
      });

      // Remove this batch from loaded video batches to allow reloading
      setLoadedVideoBatches(new Set());

      // Remove from started loading batches to allow restart
      setStartedLoadingVideoBatches(new Set());
      forceReloadBatch();
    },
    [
      containerElement,
      loadedUrls,
      setCurrentUrlBatch,
      setLoadedVideoBatches,
      setStartedLoadingVideoBatches,
    ],
  );

  const loadBatch = useCallback(
    async (batchNum) => {
      const batchNumber = parseInt(batchNum);
      const shotReps = orderedVideo.filter(
        (shotRep) => shotRep.videoBatchNumber === batchNumber,
      );
      //check if urls are loaded for this batch
      const urlsLoaded = shotReps.every((shotRep) =>
        Object.keys(loadedUrls).some((key) => key == shotRep.shotVersion.id),
      );
      if (!urlsLoaded) {
        return;
      }
      // First create all videos
      for (let i = 0; i < shotReps.length; i++) {
        const localVid = document.createElement("video");
        localVid.style.height = "100%";
        // Use consistent aria-label format: batchNumber-shotIndex
        localVid.ariaLabel = `${batchNumber}-${shotReps[i].index}`;
        localVid.style.width = "100%";
        localVid.style.marginRight = "-100%";
        localVid.style.backgroundColor = "#000000";
        localVid.style.position = "absolute";
        localVid.style.top = "0";
        localVid.style.left = "0";
        // Update video element creation to optimize for transitions
        localVid.style.transition = "opacity 0.3s ease-in-out";
        localVid.style.willChange = "opacity, transform";
        localVid.preload = "auto";
        localVid.crossOrigin = "anonymous";
        localVid.onloadedmetadata = () => {};
        localVid.onerror = (e) => {
          console.log("Video error for batch number", batchNumber, e);
          // handleUrlExpiryAndRefetch(batchNumber, shotReps[i]);
          toast.error("Video error for batch number", batchNumber, e);
          localVid.dataset.error = "true";
        };

        // Store metadata in the video element
        localVid.dataset.videoBatchNumber = batchNumber;
        localVid.dataset.urlBatchNumber = shotReps[i].urlBatchNumber;
        localVid.dataset.shotIndex = shotReps[i].index;
        localVid.dataset.shotVersion = shotReps[i].shotVersion.id;

        const isFirstInBatch =
          batchNumber === shotReps[i].videoBatchNumber &&
          shotReps[i].index === 0;
        const isCurrentTimeInRange =
          batchNumber === shotReps[i].videoBatchNumber &&
          shotReps[i].fpsStart / fps <= parseInt(seekToTime) &&
          shotReps[i].fpsEnd / fps >= parseInt(seekToTime);

        if (isFirstInBatch || isCurrentTimeInRange) {
          localVid.style.opacity = "1";
          localVid.style.zIndex = "1";
          if (isCurrentTimeInRange) {
            localVid.currentTime =
              parseInt(seekToTime) - shotReps[i].fpsStart / fps;
          }
          setIsNewVideoVisible(true);
        } else {
          localVid.style.opacity = "0";
          localVid.style.zIndex = "0";
        }

        localVid.src = loadedUrls[shotReps[i].shotVersion.id].url;
        localVid.src = loadedUrls[shotReps[i].shotVersion.id].url;
        loadedVideos.current[shotReps[i].index] = localVid;
      }

      // Then append all videos to container
      Object.values(loadedVideos.current).forEach((video) => {
        if (!containerElement.contains(video)) {
          // Only append if not already in container
          containerElement.appendChild(video);
        }
      });

      setLoadedVideoBatches((prev) => new Set([...prev, batchNumber]));
      setStartedLoadingVideoBatches(
        (prev) =>
          new Set([...prev].filter((batch) => parseInt(batch) !== batchNumber)),
      );
    },
    [orderedVideo, loadedUrls, containerElement, seekToTime],
  );

  useEffect(() => {
    const currentBatch = parseInt(currentVideoBatch);
    if (
      loadedVideoBatches.has(currentBatch) &&
      loadedVideoBatches.has(currentBatch + 1) &&
      loadedVideoBatches.size > 2
    ) {
      cleanupBatches(currentBatch);
    }
  }, [currentVideoBatch, loadedVideoBatches]);

  useEffect(() => {
    if (
      orderedVideo.length > 0 &&
      Object.keys(loadedUrls).length > 0 &&
      containerElement
    ) {
      //only load a batch it
      const currentBatchExists = Object.values(loadedUrls).some(
        (value) => value.videoBatchNumber === currentVideoBatch,
      );
      if (
        currentBatchExists &&
        !loadedVideoBatches.has(currentVideoBatch) &&
        !startedLoadingVideoBatches.has(currentVideoBatch)
      ) {
        setStartedLoadingVideoBatches(
          (prev) => new Set([...prev, currentVideoBatch]),
        );
        loadBatch(currentVideoBatch);
      }
      //   After 5 seconds, load next batch if it exists and isn't loaded
      const timer = setTimeout(() => {
        const nextBatchExists = Object.values(loadedUrls).some(
          (value) => value.videoBatchNumber === currentVideoBatch + 1,
        );
        if (
          nextBatchExists &&
          !loadedVideoBatches.has(currentVideoBatch + 1) &&
          !startedLoadingVideoBatches.has(currentVideoBatch + 1)
        ) {
          setStartedLoadingVideoBatches(
            (prev) => new Set([...prev, currentVideoBatch + 1]),
          );
          loadBatch(currentVideoBatch + 1);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [currentVideoBatch, orderedVideo, loadedUrls, containerElement]);

  useEffect(() => {
    if (
      Object.keys(loadedUrls).length === 0 &&
      loadedVideoBatches.size === 0 &&
      startedLoadingVideoBatches.size === 0 &&
      currentVideoBatch === 0 &&
      currentVideoIndex === 0
    ) {
      setResetAll((prev) => prev + 1);
    }
  }, [
    loadedVideos,
    loadedVideoBatches,
    startedLoadingVideoBatches,
    currentVideoBatch,
    currentVideoIndex,
  ]);

  const resetVideoBatchStates = useCallback(() => {
    setCurrentTime(0);
    // Remove all video elements from DOM
    Object.values(loadedVideos.current).forEach((video) => {
      if (video.parentNode) {
        video.parentNode.removeChild(video);
      }
    });
    // Reset all states
    loadedVideos.current = {};
    setLoadedVideoBatches(new Set());
    setCurrentVideoBatch(0);
    setCurrentVideoIndex(0);
    setStartedLoadingVideoBatches(new Set());
  }, [currentVideoBatch]);

  useEffect(() => {
    return () => {
      // Tear down local video state on unmount without resetting parent currentTime,
      // so playback position survives view switches.
      Object.values(loadedVideos.current).forEach((video) => {
        if (video.parentNode) {
          video.parentNode.removeChild(video);
        }
      });
      loadedVideos.current = {};
      setLoadedVideoBatches(new Set());
      setCurrentVideoBatch(0);
      setCurrentVideoIndex(0);
      setStartedLoadingVideoBatches(new Set());
    };
  }, []);

  /*
  *
  *
  BATCH LOADING VIDEO LOGIC ENDS HERE
  *
  *
  */

  return {
    loadedVideos,
    loadedVideoBatches,
    startedLoadingVideoBatches,
    currentVideoBatch,
    isNewVideoVisible,
    playPrevious,
    playNext,
    currentVideoIndex,
    resetVideoBatchStates,
  };
};

export default useVideoBatchLoader;
